import { Alert, Box, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Controls, Handle, MarkerType, type NodeProps, Position, ReactFlow } from "@xyflow/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "../components/page-header";
import { TestSelect } from "../components/test-select";
import { useApiQuery } from "../hooks";
import { BRAND_PRIMARY, theme } from "../theme/theme";
import type { Test } from "../types";
import type { TestLineage } from "../types/lineage";

import "@xyflow/react/dist/style.css";

// Custom node component for lineage visualization
const LineageNode = ({ data, isConnectable }: NodeProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { lineage, highlighted } = data as any;

  const getTypeColor = (type: string) => {
    switch (type) {
      case "fork":
        return "primary.main";
      case "branch":
        return "secondary.main";
      case "merge":
        return "success.main";
      case "split":
        return "warning.main";
      case "extract":
        return "info.main";
      default:
        return "divider";
    }
  };

  const borderColor = getTypeColor(lineage.type);
  const backgroundColor = highlighted ? "rgba(22, 0, 234, 0.1)" : "background.paper";

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      border={highlighted ? 3 : 2}
      bgcolor={backgroundColor}
      borderColor={borderColor}
      borderRadius={2}
      p={1.5}
      minWidth={200}
      sx={{
        transition: "all 0.3s ease",
        boxShadow: highlighted ? 4 : 1,
      }}
    >
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <Stack direction="column" alignItems="center" spacing={0.5}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontSize={10}
          sx={{ textTransform: "uppercase" }}
        >
          {lineage.type}
        </Typography>
        {lineage.name && (
          <Typography
            variant="body1"
            fontWeight="bold"
            align="center"
            fontSize={13}
            maxWidth={180}
            noWrap
          >
            {lineage.name}
          </Typography>
        )}
        <Typography variant="caption" fontSize={9} color="text.secondary">
          {lineage.reason}
        </Typography>
        <Typography
          variant="caption"
          fontSize={8}
          color="text.disabled"
          sx={{ fontFamily: "monospace" }}
        >
          {lineage.id.slice(0, 8)}
        </Typography>
        <Typography variant="caption" fontSize={9}>
          {lineage.created_test_ids.length} test
          {lineage.created_test_ids.length !== 1 ? "s" : ""}
        </Typography>
      </Stack>
      <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
    </Box>
  );
};

// Custom node component for test visualization
const TestNode = ({ data, isConnectable }: NodeProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { test, highlighted, lineageType } = data as any;

  const borderColor = highlighted ? "primary.main" : "success.main";
  const backgroundColor = highlighted ? "rgba(22, 0, 234, 0.1)" : "background.paper";

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      border={highlighted ? 3 : 2}
      bgcolor={backgroundColor}
      borderColor={borderColor}
      borderRadius={2}
      p={1.5}
      minWidth={200}
      sx={{
        transition: "all 0.3s ease",
        boxShadow: highlighted ? 4 : 1,
      }}
    >
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <Stack direction="column" alignItems="center" spacing={0.5}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontSize={10}
          sx={{ textTransform: "uppercase" }}
        >
          Test
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          align="center"
          fontSize={13}
          maxWidth={180}
          noWrap
        >
          {test.name}
        </Typography>
        {lineageType && (
          <Typography variant="caption" fontSize={9} color="text.secondary">
            via {lineageType}
          </Typography>
        )}
        <Typography
          variant="caption"
          fontSize={8}
          color="text.disabled"
          sx={{ fontFamily: "monospace" }}
        >
          {test.id.slice(0, 8)}
        </Typography>
      </Stack>
      <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
    </Box>
  );
};

const nodeTypes = {
  lineage: LineageNode,
  test: TestNode,
};

// Graph layout algorithm with support for disjoint graphs
const calculateNodePositions = (lineages: TestLineage[]) => {
  const nodeMap = new Map<string, TestLineage>();
  const childrenMap = new Map<string, string[]>();
  const parentsMap = new Map<string, string[]>();

  // Build maps
  lineages.forEach((lineage) => {
    nodeMap.set(lineage.id, lineage);
    childrenMap.set(lineage.id, []);
    parentsMap.set(lineage.id, []);
  });

  // Build parent-child relationships
  lineages.forEach((lineage) => {
    if (lineage.parent_id) {
      const children = childrenMap.get(lineage.parent_id) || [];
      children.push(lineage.id);
      childrenMap.set(lineage.parent_id, children);

      const parents = parentsMap.get(lineage.id) || [];
      parents.push(lineage.parent_id);
      parentsMap.set(lineage.id, parents);
    }
  });

  // Find connected components using BFS
  const visited = new Set<string>();
  const components: string[][] = [];

  lineages.forEach((lineage) => {
    if (!visited.has(lineage.id)) {
      const component: string[] = [];
      const queue = [lineage.id];

      while (queue.length > 0) {
        const nodeId = queue.shift()!;
        if (visited.has(nodeId)) continue;

        visited.add(nodeId);
        component.push(nodeId);

        // Add both parents and children to explore the entire component
        const neighbors = [...(childrenMap.get(nodeId) || []), ...(parentsMap.get(nodeId) || [])];
        neighbors.forEach((neighbor) => {
          if (!visited.has(neighbor)) {
            queue.push(neighbor);
          }
        });
      }

      components.push(component);
    }
  });

  // Layout each component separately
  const nodeWidth = 220;
  const nodeHeight = 120;
  const horizontalSpacing = 60;
  const verticalSpacing = 200;
  const componentSpacing = 400; // Horizontal spacing between components

  const positions: { [key: string]: { x: number; y: number } } = {};

  let componentOffsetX = 0;

  components.forEach((component) => {
    // Assign levels within this component using BFS
    const levels = new Map<string, number>();
    const queue: string[] = [];

    // Find root nodes in this component
    component.forEach((nodeId) => {
      const parentId = nodeMap.get(nodeId)?.parent_id;
      if (!parentId || !component.includes(parentId)) {
        levels.set(nodeId, 0);
        queue.push(nodeId);
      }
    });

    // BFS to assign levels
    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      const level = levels.get(nodeId)!;
      const children = childrenMap.get(nodeId) || [];

      children.forEach((childId) => {
        if (component.includes(childId) && !levels.has(childId)) {
          levels.set(childId, level + 1);
          queue.push(childId);
        }
      });
    }

    // Group nodes by level within this component
    const nodesByLevel = new Map<number, string[]>();
    levels.forEach((level, nodeId) => {
      const nodes = nodesByLevel.get(level) || [];
      nodes.push(nodeId);
      nodesByLevel.set(level, nodes);
    });

    // Calculate max width of this component
    let maxWidth = 0;
    nodesByLevel.forEach((nodeIds) => {
      const levelWidth = nodeIds.length * nodeWidth + (nodeIds.length - 1) * horizontalSpacing;
      maxWidth = Math.max(maxWidth, levelWidth);
    });

    // Position nodes in this component
    nodesByLevel.forEach((nodeIds, level) => {
      const levelCount = nodeIds.length;
      const totalWidth = levelCount * nodeWidth + (levelCount - 1) * horizontalSpacing;
      const startX = componentOffsetX - totalWidth / 2;

      nodeIds.forEach((nodeId, index) => {
        const x = startX + index * (nodeWidth + horizontalSpacing);
        const y = level * (nodeHeight + verticalSpacing);
        positions[nodeId] = { x, y };
      });
    });

    // Move offset for next component
    componentOffsetX += maxWidth + componentSpacing;
  });

  return positions;
};

const LineagePage: React.FC = () => {
  const {
    data: lineageData,
    isLoading: isLoadingLineage,
    error,
  } = useApiQuery<TestLineage[]>("/lineage", {
    refetchInterval: false,
  });

  const { data: tests } = useApiQuery<Test[]>("/tests", {
    refetchInterval: false,
  });

  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<"lineage" | "tests">("lineage");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const highlightedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Find the lineage_id for the selected test
  const selectedLineageId = useMemo(() => {
    if (!selectedTestId || !tests) return null;
    const test = tests.find((t) => t.id === selectedTestId);
    return test?.lineage_id || null;
  }, [selectedTestId, tests]);

  // Calculate graph positions
  const graphPositions = useMemo(() => {
    if (!lineageData) return {};
    return calculateNodePositions(lineageData);
  }, [lineageData]);

  // Build nodes
  const flowNodes = useMemo(() => {
    if (!lineageData) return [];
    return lineageData.map((lineage) => ({
      id: lineage.id,
      type: "lineage",
      position: {
        x: graphPositions[lineage.id]?.x ?? 0,
        y: graphPositions[lineage.id]?.y ?? 0,
      },
      data: {
        lineage,
        highlighted: selectedLineageId === lineage.id,
      },
    }));
  }, [lineageData, graphPositions, selectedLineageId]);

  // Build edges
  const flowEdges = useMemo(() => {
    if (!lineageData) return [];
    return lineageData
      .filter((lineage) => lineage.parent_id)
      .map((lineage) => {
        const isHighlighted = selectedLineageId === lineage.id;
        return {
          type: "smoothstep",
          id: `${lineage.parent_id}-${lineage.id}`,
          source: lineage.parent_id!,
          target: lineage.id,
          label: lineage.type,
          animated: isHighlighted,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isHighlighted ? BRAND_PRIMARY : theme.palette.text.disabled,
          },
          style: {
            stroke: isHighlighted ? BRAND_PRIMARY : theme.palette.text.disabled,
            strokeWidth: isHighlighted ? 3 : 1.5,
          },
          labelStyle: {
            fontSize: 10,
            fontWeight: isHighlighted ? "bold" : "normal",
          },
        };
      });
  }, [lineageData, selectedLineageId]);

  // Build test nodes and edges based on lineage with support for disjoint graphs
  const testGraphPositions = useMemo(() => {
    if (!lineageData || !tests) return {};

    // Get all tests that have lineage
    const lineageTests = tests.filter((test) => test.lineage_id);

    // Build a graph of test relationships based on lineage
    const testChildrenMap = new Map<string, string[]>();
    const testParentsMap = new Map<string, string[]>();

    lineageTests.forEach((test) => {
      testChildrenMap.set(test.id, []);
      testParentsMap.set(test.id, []);
    });

    // Build test relationships based on lineage hierarchy
    lineageData.forEach((lineage) => {
      if (lineage.parent_id) {
        const parentLineage = lineageData.find((l) => l.id === lineage.parent_id);
        if (parentLineage) {
          // Connect tests from parent lineage to tests from current lineage
          parentLineage.created_test_ids.forEach((parentTestId) => {
            lineage.created_test_ids.forEach((childTestId) => {
              const parentTest = lineageTests.find((t) => t.id === parentTestId);
              const childTest = lineageTests.find((t) => t.id === childTestId);

              if (parentTest && childTest) {
                const children = testChildrenMap.get(parentTestId) || [];
                children.push(childTestId);
                testChildrenMap.set(parentTestId, children);

                const parents = testParentsMap.get(childTestId) || [];
                parents.push(parentTestId);
                testParentsMap.set(childTestId, parents);
              }
            });
          });
        }
      }
    });

    // Find connected components
    const visited = new Set<string>();
    const components: string[][] = [];

    lineageTests.forEach((test) => {
      if (!visited.has(test.id)) {
        const component: string[] = [];
        const queue = [test.id];

        while (queue.length > 0) {
          const testId = queue.shift()!;
          if (visited.has(testId)) continue;

          visited.add(testId);
          component.push(testId);

          // Add both parents and children to explore the entire component
          const neighbors = [
            ...(testChildrenMap.get(testId) || []),
            ...(testParentsMap.get(testId) || []),
          ];
          neighbors.forEach((neighbor) => {
            if (!visited.has(neighbor)) {
              queue.push(neighbor);
            }
          });
        }

        components.push(component);
      }
    });

    // Layout each component separately
    const nodeWidth = 220;
    const nodeHeight = 120;
    const horizontalSpacing = 60;
    const verticalSpacing = 200;
    const componentSpacing = 400;

    const positions: { [key: string]: { x: number; y: number } } = {};
    let componentOffsetX = 0;

    components.forEach((component) => {
      // Assign levels within this component
      const levels = new Map<string, number>();
      const queue: string[] = [];

      // Find root nodes in this component (tests with no parents in this component)
      component.forEach((testId) => {
        const parents = testParentsMap.get(testId) || [];
        const hasParentInComponent = parents.some((p) => component.includes(p));
        if (!hasParentInComponent) {
          levels.set(testId, 0);
          queue.push(testId);
        }
      });

      // BFS to assign levels
      while (queue.length > 0) {
        const testId = queue.shift()!;
        const level = levels.get(testId)!;
        const children = testChildrenMap.get(testId) || [];

        children.forEach((childId) => {
          if (component.includes(childId) && !levels.has(childId)) {
            levels.set(childId, level + 1);
            queue.push(childId);
          }
        });
      }

      // Group tests by level within this component
      const testsByLevel = new Map<number, string[]>();
      levels.forEach((level, testId) => {
        const tests = testsByLevel.get(level) || [];
        tests.push(testId);
        testsByLevel.set(level, tests);
      });

      // Calculate max width of this component
      let maxWidth = 0;
      testsByLevel.forEach((testIds) => {
        const levelWidth = testIds.length * nodeWidth + (testIds.length - 1) * horizontalSpacing;
        maxWidth = Math.max(maxWidth, levelWidth);
      });

      // Position nodes in this component
      testsByLevel.forEach((testIds, level) => {
        const levelCount = testIds.length;
        const totalWidth = levelCount * nodeWidth + (levelCount - 1) * horizontalSpacing;
        const startX = componentOffsetX - totalWidth / 2;

        testIds.forEach((testId, index) => {
          const x = startX + index * (nodeWidth + horizontalSpacing);
          const y = level * (nodeHeight + verticalSpacing);
          positions[testId] = { x, y };
        });
      });

      // Move offset for next component
      componentOffsetX += maxWidth + componentSpacing;
    });

    return positions;
  }, [lineageData, tests]);

  const testFlowNodes = useMemo(() => {
    if (!lineageData || !tests) return [];

    const lineageTests = tests.filter((test) => test.lineage_id);

    return lineageTests.map((test) => {
      const lineage = lineageData.find((l) => l.id === test.lineage_id);
      return {
        id: test.id,
        type: "test",
        position: {
          x: testGraphPositions[test.id]?.x ?? 0,
          y: testGraphPositions[test.id]?.y ?? 0,
        },
        data: {
          test,
          highlighted: selectedTestId === test.id,
          lineageType: lineage?.type,
        },
      };
    });
  }, [lineageData, tests, testGraphPositions, selectedTestId]);

  const testFlowEdges = useMemo(() => {
    if (!lineageData || !tests) return [];

    const edges: Array<{
      type: string;
      id: string;
      source: string;
      target: string;
      label: string;
      animated: boolean;
      markerEnd: { type: MarkerType; color: string };
      style: { stroke: string; strokeWidth: number };
      labelStyle: { fontSize: number; fontWeight: string };
    }> = [];

    // Build edges based on lineage parent-child relationships
    lineageData.forEach((lineage) => {
      if (lineage.parent_id) {
        const parentLineage = lineageData.find((l) => l.id === lineage.parent_id);
        if (parentLineage) {
          // Connect all tests from parent lineage to all tests from current lineage
          parentLineage.created_test_ids.forEach((parentTestId) => {
            lineage.created_test_ids.forEach((childTestId) => {
              const parentTest = tests.find((t) => t.id === parentTestId);
              const childTest = tests.find((t) => t.id === childTestId);

              if (parentTest && childTest) {
                const isHighlighted =
                  selectedTestId === childTestId || selectedTestId === parentTestId;
                edges.push({
                  type: "smoothstep",
                  id: `${parentTestId}-${childTestId}`,
                  source: parentTestId,
                  target: childTestId,
                  label: lineage.type,
                  animated: isHighlighted,
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: isHighlighted ? BRAND_PRIMARY : theme.palette.text.disabled,
                  },
                  style: {
                    stroke: isHighlighted ? BRAND_PRIMARY : theme.palette.text.disabled,
                    strokeWidth: isHighlighted ? 3 : 1.5,
                  },
                  labelStyle: {
                    fontSize: 10,
                    fontWeight: isHighlighted ? "bold" : "normal",
                  },
                });
              }
            });
          });
        }
      }
    });

    return edges;
  }, [lineageData, tests, selectedTestId]);

  // Center on selected node when it changes
  useEffect(() => {
    if (reactFlowInstance) {
      // Clear any existing timeout
      if (highlightedTimeoutRef.current) {
        clearTimeout(highlightedTimeoutRef.current);
      }

      // Small delay to ensure nodes are rendered
      highlightedTimeoutRef.current = setTimeout(() => {
        if (viewTab === "lineage" && selectedLineageId) {
          const node = flowNodes.find((n) => n.id === selectedLineageId);
          if (node) {
            reactFlowInstance.setCenter(node.position.x + 110, node.position.y + 60, {
              zoom: 1,
              duration: 800,
            });
          }
        } else if (viewTab === "tests" && selectedTestId) {
          const node = testFlowNodes.find((n) => n.id === selectedTestId);
          if (node) {
            reactFlowInstance.setCenter(node.position.x + 110, node.position.y + 60, {
              zoom: 1,
              duration: 800,
            });
          }
        }
      }, 100);
    }

    return () => {
      if (highlightedTimeoutRef.current) {
        clearTimeout(highlightedTimeoutRef.current);
      }
    };
  }, [selectedLineageId, selectedTestId, viewTab, reactFlowInstance, flowNodes, testFlowNodes]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onInit = useCallback((instance: any) => {
    setReactFlowInstance(instance);
  }, []);

  if (isLoadingLineage) {
    return (
      <Box data-testid="lineage-page">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <CircularProgress size={16} />
          <Typography ml={2}>Loading lineage data...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box data-testid="lineage-page">
        <Alert severity="error">Could not load lineage: {error.message || "Unknown error"}</Alert>
      </Box>
    );
  }

  if (!lineageData || lineageData.length === 0) {
    return (
      <Box data-testid="lineage-page">
        <PageHeader title="Test Lineage" subtitle="Version control for your tests" />
        <Alert severity="info">
          No lineage data available yet. Create test forks, branches, or merges to see the lineage
          graph.
        </Alert>
      </Box>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentNodes: any = viewTab === "lineage" ? flowNodes : testFlowNodes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentEdges: any = viewTab === "lineage" ? flowEdges : testFlowEdges;

  return (
    <Box data-testid="lineage-page">
      <PageHeader
        title="Test Lineage"
        subtitle="Track the evolution and relationships of your tests"
      />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Tabs
          value={viewTab}
          onChange={(_, newValue) => setViewTab(newValue)}
          sx={{ minHeight: "36px" }}
        >
          <Tab
            label="Lineage View"
            value="lineage"
            sx={{
              minHeight: "36px",
              textTransform: "none",
              fontSize: "0.875rem",
            }}
          />
          <Tab
            label="Tests View"
            value="tests"
            sx={{
              minHeight: "36px",
              textTransform: "none",
              fontSize: "0.875rem",
            }}
          />
        </Tabs>
        <Box sx={{ flex: 1, maxWidth: 400 }}>
          <TestSelect
            label="Select Test"
            placeholder="Find a test to highlight..."
            value={selectedTestId}
            onChange={setSelectedTestId}
            size="small"
          />
        </Box>
      </Stack>
      <Box
        width="100%"
        height="600px"
        border={1}
        borderColor="divider"
        borderRadius={2}
        sx={{ backgroundColor: "grey.50" }}
      >
        <ReactFlow
          key={viewTab}
          nodes={currentNodes}
          edges={currentEdges}
          onInit={onInit}
          fitView
          fitViewOptions={{
            padding: 0.2,
            minZoom: 0.1,
            maxZoom: 2,
          }}
          minZoom={0.1}
          maxZoom={4}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          nodeTypes={nodeTypes}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showZoom showFitView showInteractive />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default LineagePage;
